import { RouteObject, Navigate } from 'react-router-dom'
import { getRouteComponent } from './routeRegistry'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { ClientLayout } from '@/components/layout/ClientLayout' 

interface UserRoute {
  id?: number
  path: string
  name: string
  icon?: string
  side_nav?: string
  children?: UserRoute[]
}

/**
 * Generate React Router routes from user_routes (database navigation)
 */
export function generateRoutesFromUserRoutes(
  userRoutes: UserRoute[],
  publicRoutes: RouteObject[] = []
): RouteObject[] {
  const routes: RouteObject[] = [
    // Public routes (always available)
    ...publicRoutes,
    {
      path: '/',
      element: <Navigate to="/dashboard" replace />,
    },
  ]

  /**
   * Recursively process routes and their children
   */
  function processRoute(route: UserRoute, parentPath: string = '') {
    let fullPath: string
    
    if (route.path.startsWith('/')) {
      // Absolute path - check if it exists in route registry
      // If not, try joining with parentPath (for cases like /users under /access-control)
      const absolutePath = route.path
      const ComponentForAbsolute = getRouteComponent(absolutePath)
      
      if (ComponentForAbsolute) {
        // Absolute path exists in registry (e.g., /shipping-lines/create)
        fullPath = absolutePath
      } else if (parentPath) {
        // Absolute path doesn't exist, try joining with parent
        // (e.g., /users under /access-control becomes /access-control/users)
        const joinedPath = `${parentPath}${absolutePath}`
        const ComponentForJoined = getRouteComponent(joinedPath)
        if (ComponentForJoined) {
          fullPath = joinedPath
        } else {
          // Neither absolute nor joined path exists, use absolute as fallback
          fullPath = absolutePath
        }
      } else {
        // No parent path, use absolute as-is
        fullPath = absolutePath
      }
    } else {
      // Relative path - join with parentPath
      fullPath = parentPath ? `${parentPath}${route.path}` : route.path
    }

    // Get component for this route
    const Component = getRouteComponent(fullPath)
    
    const alreadyRegistered = routes.some((r) => r.path === fullPath)

    if (Component && !alreadyRegistered) {
      routes.push({
        path: fullPath,
        element: (
          <AuthGuard>
            <Component />
          </AuthGuard>
        ),
      })
    }

    // Process children
    // Use fullPath as the parent path for children
    // fullPath is always set (either absolute, joined, or relative path)
    // This ensures children are processed with the correct parent context
    // For example: /access-control (parent) -> /users (child) becomes /access-control/users
    if (route.children && route.children.length > 0) {
      route.children.forEach((child) => {
        // Pass fullPath as parent so children can be joined correctly
        // The processRoute function will:
        // 1. Check if child's absolute path exists in registry (e.g., /shipping-lines/create)
        // 2. If not, try joining with parentPath (e.g., /users + /access-control = /access-control/users)
        processRoute(child, fullPath)
      })
    }
  }

  // Process all user routes
  userRoutes.forEach((route) => {
    processRoute(route)
  })

  // Always ensure dashboard route exists (even if user_routes is empty or doesn't include it)
  const hasDashboard = routes.some((r) => r.path === '/dashboard')
  if (!hasDashboard) {
    const DashboardComponent = getRouteComponent('/dashboard')
    if (DashboardComponent) {
      routes.push({
        path: '/dashboard',
        element: (
          <AuthGuard>
            <DashboardComponent />
          </AuthGuard>
        ),
      })
    }
  }

  // Always ensure help-center route exists (FAQ / help page)
  const hasHelpCenter = routes.some((r) => r.path === '/help-center')
  if (!hasHelpCenter) {
    const HelpCenterComponent = getRouteComponent('/help-center')
    if (HelpCenterComponent) {
      routes.push({
        path: '/help-center',
        element: (
          <AuthGuard>
            <HelpCenterComponent />
          </AuthGuard>
        ),
      })
    }
  }

  // Always ensure user activity route exists (it's accessed via link, not from user_routes)
  // Add it BEFORE the catch-all route so it can be matched
  const hasUserActivity = routes.some((r) => r.path === '/access-control/user-activity/:id')
    if (!hasUserActivity) {
    const UserActivityComponent = getRouteComponent('/access-control/user-activity/:id')
    if (UserActivityComponent) {
      // Insert before catch-all route (if it exists) or at the end
      const catchAllIndex = routes.findIndex((r) => r.path === '*')
      if (catchAllIndex >= 0) {
        routes.splice(catchAllIndex, 0, {
          path: '/access-control/user-activity/:id',
          element: (
            <AuthGuard>
              <UserActivityComponent />
            </AuthGuard>
          ),
        })
      } else {
        routes.push({
          path: '/access-control/user-activity/:id',
          element: (
            <AuthGuard>
              <UserActivityComponent />
            </AuthGuard>
          ),
        })
      }
    }
  }

  // Fallback to dashboard for any unmatched authenticated route
  // Only add catch-all if we have routes (don't redirect during initialization)
  if (routes.length > publicRoutes.length + 1) { // More than just public routes + root redirect
    routes.push({
      path: '*',
      element: <Navigate to="/dashboard" replace />,
    })
  }

  return routes
}

